import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import Keyboard from "./Keyboard";
import { AlreadyPlayedPage } from "@precisionpros/game-ui";
import { connectProGame } from "./ConnectProBrand";

const formatTime = (ms) => {
  const totalMs = ms % 1000;
  const totalSec = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${totalMs.toString().padStart(3, '0')}`;
};

const ARIZONA = "America/Phoenix";
const hubUrl = () => (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3000' : 'https://games.precisionpros.com';

const hubHeaders = () => {
  const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
  if (token) localStorage.setItem('hubToken', token);
  return token ? { 'X-Hub-Token': token } : {};
};

// Reusable style objects
const styles = {
  pageWrapper: {
    minHeight: "100vh",
    backgroundColor: "#fafbfc",
    padding: "12px 12px 300px 12px",
    paddingBottom: "max(300px, env(safe-area-inset-bottom) + 300px)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif",
  },
  cardContainer: {
    maxWidth: "500px",
    margin: "0 auto",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06)",
    padding: "16px 12px 8px 12px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: "10px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "800",
    margin: 0,
    color: "#0a0a0a",
    letterSpacing: "-0.5px",
  },
  leaderboardLink: {
    fontSize: "13px",
    color: "#888",
    textDecoration: "none",
    transition: "all 0.2s ease",
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: "8px",
    fontWeight: "500",
  },
  metaRow: {
    fontSize: "10px",
    color: "#aaa",
    marginBottom: "8px",
    display: "flex",
    gap: "8px",
    fontWeight: "500",
  },
  statBar: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
    marginBottom: "10px",
    padding: "0",
    backgroundColor: "transparent",
    borderRadius: "0",
  },
  statItem: {
    textAlign: "center",
    padding: "8px 6px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #f0f0f0",
  },
  statLabel: {
    fontSize: "8px",
    fontWeight: "700",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "2px",
  },
  statValue: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#0a0a0a",
  },
  wordBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    height: "50px",
    padding: "0 12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    fontSize: "22px",
    fontWeight: "900",
    color: "#0a0a0a",
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    margin: "4px 0",
    border: "2px solid #e5e7eb",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    boxSizing: "border-box",
  },
  wordInputsContainer: {
    marginBottom: "6px",
  },
  wordInput: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    height: "50px",
    margin: "4px 0",
    padding: "0 12px",
    fontSize: "22px",
    fontWeight: "900",
    textAlign: "left",
    textTransform: "uppercase",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    transition: "all 0.15s ease",
    letterSpacing: "1.2px",
    boxSizing: "border-box",
    color: "#000",
    position: "relative",
  },
  buttonContainer: {
    display: "flex",
    gap: "0",
    marginBottom: "2px",
  },
  primaryButton: {
    flex: 1,
    padding: "14px 16px",
    fontSize: "15px",
    fontWeight: "700",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "800",
  },
  primaryButtonStart: {
    backgroundColor: "#2563eb",
    color: "white",
  },
  primaryButtonSubmit: {
    backgroundColor: "#16a34a",
    color: "white",
  },
  secondaryButton: {
    padding: "0 14px",
    fontSize: "13px",
    fontWeight: "700",
    border: "1.5px solid #d0d0d0",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: "white",
    color: "#333",
    transition: "all 0.15s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "80px",
  },
  keyboardWrapper: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fafbfc",
    borderTop: "1px solid #e5e7eb",
    paddingBottom: "env(safe-area-inset-bottom)",
    zIndex: 1000,
    boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
  },
};

const ConnectProGame = () => {
  const [puzzleId, setPuzzleId] = useState(null);
  const [startWord, setStartWord] = useState("");
  const [endWord, setEndWord] = useState("");
  const [chainLength, setChainLength] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [previousSubmissions, setPreviousSubmissions] = useState(new Set());
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [correctWords, setCorrectWords] = useState(null);
  const [hintedPositions, setHintedPositions] = useState([]);
  const [guessCount, setGuessCount] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [activeRow, setActiveRow] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [playerName, setPlayerName] = useState(localStorage.getItem("playerName") || "");
  const [hasPlayedToday, setHasPlayedToday] = useState(false);
  const [gameCompletionStatus, setGameCompletionStatus] = useState(null); // 'started' or 'completed'
  const [canResume, setCanResume] = useState(false); // true if valid in-progress game exists
  const puzzleDate = DateTime.now().setZone(ARIZONA).toISODate();
  const isLocalDev = process.env.REACT_APP_API_URL.includes("localhost") || process.env.REACT_APP_API_URL.includes("127.0.0.1");
  const buildProDomain = isLocalDev ? "http://localhost:3003" : "https://buildpro.precisionpros.com";
  const buildProLink = `${buildProDomain}/?player_name=${encodeURIComponent(playerName || "")}`;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramName = params.get("player_name");
    if (paramName) {
      localStorage.setItem("playerName", paramName);
      setPlayerName(paramName);
    }
  }, []);

  useEffect(() => {
    const fetchPuzzle = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const puzzleIdParam = params.get("puzzle_id");

        const endpoint = puzzleIdParam
          ? `${process.env.REACT_APP_API_URL}/connectpro/puzzle/?puzzle_id=${puzzleIdParam}`
          : `${process.env.REACT_APP_API_URL}/connectpro/daily/`;

        const response = await fetch(endpoint);
        const data = await response.json();
        setPuzzleId(data.id);
        setStartWord(data.start_word);
        setEndWord(data.end_word);
        setChainLength(data.chain_length);

        // Initialize arrays dynamically based on chain_length - 2
        const rowCount = data.chain_length - 2;
        setGuesses(Array(rowCount).fill(""));
        setValidationResults(Array(rowCount).fill(null));
        setHintedPositions(Array(rowCount).fill(0));
        setPreviousSubmissions(new Set());
        setDuplicateMessage("");
      } catch {
        setError("Failed to load puzzle.");
      } finally {
        setLoading(false);
      }
    };

    fetchPuzzle();
  }, []);

  // Check if player has already played today (via API for completed puzzles, or localStorage for in-progress games)
  useEffect(() => {
    if (!playerName) return;

    // If there's a saved game state for today, don't check API - localStorage takes precedence
    const savedGameState = localStorage.getItem("connectproGameInProgress");
    if (savedGameState) {
      try {
        const gameState = JSON.parse(savedGameState);
        const today = DateTime.now().setZone(ARIZONA).toISODate();
        if (gameState.date === today) {
          console.log("🎮 [API-CHECK] Skipping API check, using localStorage state");
          return;
        }
      } catch (e) {
        // If can't parse, proceed with API check
      }
    }

    const checkPlayedToday = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/connectpro/has-played-today/?player_name=${encodeURIComponent(playerName)}`
        );
        const data = await response.json();
        if (data.has_played_today === true) {
          setHasPlayedToday(true);
          setGameCompletionStatus("completed"); // API only returns true for solved games
          console.log("🎮 [API-CHECK] has_played_today from API: true (setting status to completed)");
        }
      } catch {
        // Fail silently
      }
    };

    checkPlayedToday();
  }, [playerName]);

  // Check if game is already in progress (user clicked Start but refreshed before completing)
  useEffect(() => {
    if (!puzzleId || !playerName) return; // Wait for puzzle and player to load

    const savedGameState = localStorage.getItem("connectproGameInProgress");
    if (savedGameState) {
      try {
        const gameState = JSON.parse(savedGameState);
        const today = DateTime.now().setZone(ARIZONA).toISODate();

        // Check if saved game matches: same puzzle_date, same puzzleId, and same player_name
        if (gameState.date === today && gameState.puzzleId === puzzleId && gameState.player_name === playerName) {
          // If puzzle is already completed, block any further attempts
          if (gameState.completed) {
            setHasPlayedToday(true);
            setGameCompletionStatus("completed");
            console.log("🎮 [RESTORE] Puzzle already completed, blocking further attempts");
          } else {
            // Game was started but not completed - fetch progress from backend and restore
            setHasPlayedToday(true);
            setCanResume(true);
            setStartTime(gameState.startedAt);
            setTimerRunning(true);

            // Fetch the current game progress from backend to restore guesses/hints
            fetch(`${process.env.REACT_APP_API_URL}/connectpro/game-progress/?puzzle_id=${puzzleId}&player_name=${encodeURIComponent(playerName)}`)
              .then(res => res.json())
              .then(data => {
                if (data.in_progress) {
                  console.log("🎮 [RESTORE] Fetched game progress from backend");
                  setGuesses(data.guesses || []);
                  setValidationResults(data.validation_results || []);
                  setGuessCount(data.guess_count || 0);
                  setHintsUsed(data.hints_used || 0);

                  // Find first incomplete row to set active
                  const firstIncomplete = (data.validation_results || []).findIndex(v => v !== true);
                  if (firstIncomplete !== -1) {
                    setActiveRow(firstIncomplete);
                  }

                  setGameStarted(true);
                  console.log("🎮 [RESTORE] Restored game state: guesses=" + data.guess_count + ", hints=" + data.hints_used);
                } else {
                  console.log("🎮 [RESTORE] No in-progress game found on backend, starting fresh");
                  setGameStarted(true);
                }
              })
              .catch(err => {
                console.error("❌ [RESTORE] Error fetching game progress:", err);
                // Fallback: start game anyway with current state
                setGameStarted(true);
              });
          }
        } else {
          // Game is from a different day, puzzle, or player - clear it
          console.log("🎮 [RESTORE] Saved game doesn't match (different date/puzzle/player), clearing");
          localStorage.removeItem("connectproGameInProgress");
        }
      } catch (e) {
        console.error("❌ [RESTORE] Error parsing saved game state:", e);
        localStorage.removeItem("connectproGameInProgress");
      }
    }
  }, [puzzleId, playerName]);

  useEffect(() => {
    if (startTime && timerRunning) {
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [startTime, timerRunning]);

  // Show completion modal when all rows are correct
  useEffect(() => {
    if (gameStarted && validationResults.every((v) => v === true) && !showCompletionModal) {
      setShowCompletionModal(true);
    }
  }, [validationResults, gameStarted, showCompletionModal]);

  const handleStartGame = () => {
    // Mark as played today IMMEDIATELY - this counts as having played
    setHasPlayedToday(true);
    console.log("🎮 [START] Marked hasPlayedToday = true (clicked Start button)");

    // Save game state to localStorage so refresh doesn't allow fresh attempt
    const today = DateTime.now().setZone(ARIZONA).toISODate();
    const gameState = {
      date: today,
      puzzleId: puzzleId,
      player_name: playerName,
      startedAt: Date.now(),
    };
    localStorage.setItem("connectproGameInProgress", JSON.stringify(gameState));
    console.log("🎮 [START] Game started, saved to localStorage");

    setGameStarted(true);
    setStartTime(Date.now());
    setTimerRunning(true);
  };


  // Per-row hint handler
  const handleRowHint = async (rowIdx) => {
    if (!gameStarted) return;
    // Don't hint locked (correct) rows
    if (validationResults[rowIdx] === true) return;

    let cw = correctWords;
    if (!cw) {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/connectpro/answers/?puzzle_id=${puzzleId}`);
        const data = await res.json();
        cw = data.words;
        setCorrectWords(cw);
      } catch {
        return;
      }
    }

    const currentText = guesses[rowIdx];
    const correctText = cw[rowIdx];
    if (!correctText) return;
    const correctTextUpper = correctText.toUpperCase();

    // Step 1: Find the longest leading sequence of characters that match the correct answer
    // Compare against uppercase version since user types in uppercase
    let matchPos = 0;
    while (matchPos < currentText.length && matchPos < correctTextUpper.length && currentText[matchPos] === correctTextUpper[matchPos]) {
      matchPos++;
    }

    // Step 2 & 3: Determine what to reveal
    let newText;
    if (matchPos >= correctTextUpper.length) {
      // Entire correct word is already matched (possibly with extra letters after)
      // Hint clears the extras and completes the word
      newText = correctTextUpper;
    } else {
      // Reveal the next correct letter
      newText = correctTextUpper.slice(0, matchPos + 1);
    }

    // Update guesses
    const newGuesses = [...guesses];
    newGuesses[rowIdx] = newText;
    setGuesses(newGuesses);

    // Update hinted positions to protect these letters from keyboard edits
    const newHintedPositions = [...hintedPositions];
    newHintedPositions[rowIdx] = matchPos + 1;
    setHintedPositions(newHintedPositions);

    // Increment hint counter
    const newHintsUsed = hintsUsed + 1;
    setHintsUsed(newHintsUsed);

    // Check if this hint completed the row (compare against uppercase version)
    if (newText === correctTextUpper) {
      // Mark row as correct/locked
      const newValidationResults = [...validationResults];
      newValidationResults[rowIdx] = true;
      setValidationResults(newValidationResults);

      // Reset this row's hinted positions since it's now complete
      const newHintedPositions = [...hintedPositions];
      newHintedPositions[rowIdx] = 0;
      setHintedPositions(newHintedPositions);

      // Auto-advance focus to next unresolved row
      const nextUnresolvedRow = newValidationResults.findIndex((v) => v !== true);
      if (nextUnresolvedRow !== -1) {
        setActiveRow(nextUnresolvedRow);
      }

      // Check if puzzle is now completely solved
      if (newValidationResults.every((v) => v === true)) {
        setTimerRunning(false);
        setSubmitted(true);

        // Mark completion in localStorage
        const today = DateTime.now().setZone(ARIZONA).toISODate();
        const completedState = {
          date: today,
          puzzleId: puzzleId,
          player_name: playerName,
          completed: true,
        };
        localStorage.setItem("connectproGameInProgress", JSON.stringify(completedState));

        // Save result immediately
        fetch(`${process.env.REACT_APP_API_URL}/connectpro/save-result/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...hubHeaders() },
          body: JSON.stringify({
            puzzle_id: puzzleId,
            player_name: playerName,
            solved: true,
            time_taken: elapsedTime / 1000,
            guess_count: guessCount,
            hints_used: newHintsUsed,
            current_guesses: newGuesses,
            current_correct: newValidationResults,
          }),
        }).catch(() => {});
      }
    }
  };

  // Keyboard handlers
  const handleKeyboardLetter = (letter) => {
    if (!gameStarted) return;
    // Prevent input into locked (correct) rows
    if (validationResults[activeRow] === true) return;

    setGuesses((prevGuesses) => {
      const newGuesses = [...prevGuesses];
      const numHinted = hintedPositions[activeRow] || 0;
      const currentGuess = newGuesses[activeRow] || "";

      let newGuess = (currentGuess + letter).toUpperCase();

      // Preserve hinted prefix when typing
      if (numHinted > 0 && correctWords) {
        const prefix = correctWords[activeRow].toUpperCase().slice(0, numHinted);
        if (!newGuess.startsWith(prefix)) {
          newGuess = prefix + newGuess.slice(numHinted);
        }
      }

      newGuesses[activeRow] = newGuess;
      return newGuesses;
    });
  };

  const handleKeyboardBackspace = () => {
    if (!gameStarted) return;
    // Prevent input into locked (correct) rows
    if (validationResults[activeRow] === true) return;

    setGuesses((prevGuesses) => {
      const newGuesses = [...prevGuesses];
      const numHinted = hintedPositions[activeRow] || 0;
      const currentGuess = newGuesses[activeRow] || "";

      // Prevent backspacing hinted letters
      if (currentGuess.length > numHinted) {
        newGuesses[activeRow] = currentGuess.slice(0, -1);
      }

      return newGuesses;
    });
  };

  const handleKeyboardClear = () => {
    if (!gameStarted) return;
    setGuesses((prevGuesses) => prevGuesses.map((g, i) => {
      if (validationResults[i] === true) return g; // frozen row
      const numHinted = hintedPositions[i];
      if (numHinted > 0 && correctWords) {
        return correctWords[i].toUpperCase().slice(0, numHinted);
      }
      return "";
    }));
  };

  const handleKeyboardEnter = () => {
    if (!gameStarted) return;
    handleSubmit();
  };


  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError("");

    const submissionKey = JSON.stringify(guesses);
    if (previousSubmissions.has(submissionKey)) {
      setDuplicateMessage("Already tried that combination!");
      setTimeout(() => setDuplicateMessage(""), 2500);
      return;
    }
    setPreviousSubmissions(prev => new Set([...prev, submissionKey]));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/connectpro/validate/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ puzzle_id: puzzleId, guesses, time_taken: elapsedTime / 1000 }),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error("❌ Failed to parse JSON:", e);
        setError("Server error. Try again later.");
        return;
      }

      if (!response.ok) {
        console.error("❌ Response not OK", data);
        setError(data?.error || "Unexpected error from server.");
        return;
      }

      if (data.details && Array.isArray(data.details)) {
        const nextGuessCount = guessCount + 1;
        setGuessCount(nextGuessCount);
        setValidationResults(data.details);
        setSubmitted(true);

        // Preserve hinted positions for incorrect rows; only reset for rows now correct
        const newHintedPositions = [...hintedPositions];
        data.details.forEach((isCorrect, idx) => {
          if (isCorrect) {
            newHintedPositions[idx] = 0;
          }
        });
        setHintedPositions(newHintedPositions);

        // Auto-advance focus to next incomplete row after submission
        const nextIncompleteRow = data.details.findIndex(
          (isCorrect, idx) => isCorrect !== true
        );
        if (nextIncompleteRow !== -1) {
          setActiveRow(nextIncompleteRow);
        }
        // If all rows correct, activeRow stays on current position (ready to review)

        if (data.correct) {
          setTimerRunning(false);
          // Mark completion in localStorage for restoration logic
          const today = DateTime.now().setZone(ARIZONA).toISODate();
          const completedState = {
            date: today,
            puzzleId: puzzleId,
            player_name: playerName,
            completed: true,
          };
          localStorage.setItem("connectproGameInProgress", JSON.stringify(completedState));
          console.log("🎮 [COMPLETE] Puzzle solved, saved completion state to localStorage");
        }

        // Always save after every submit so unsolved attempts appear on the leaderboard
        fetch(`${process.env.REACT_APP_API_URL}/connectpro/save-result/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...hubHeaders() },
          body: JSON.stringify({
            puzzle_id: puzzleId,
            player_name: playerName,
            solved: data.correct,
            time_taken: data.correct ? elapsedTime / 1000 : null,
            guess_count: nextGuessCount,
            hints_used: hintsUsed,
            current_guesses: guesses,
            current_correct: data.details,
          }),
        }).catch(() => {});
      } else {
        setError("Unexpected response format from server.");
      }
    } catch (err) {
      console.error("❌ Exception:", err);
      setError("Network or server error occurred.");
    }
  };

  // Login gate
  if (!playerName) {
    return <ConnectProLoginScreen onLogin={setPlayerName} />;
  }

  if (loading) return <div style={styles.pageWrapper}><p>Loading...</p></div>;
  if (error) return <div style={styles.pageWrapper}><p style={{ color: "#d32f2f" }}>{error}</p></div>;

  // Already played today check - but only block if no valid resumable game and no active game in current session
  if (hasPlayedToday && !canResume && !gameStarted) {
    const isSolved = gameCompletionStatus === "completed";
    return <AlreadyPlayedPage
      game={connectProGame}
      title={isSolved ? "You've already solved today's puzzle!" : "You already played today!"}
      message="Come back tomorrow for a new puzzle."
      icon={isSolved ? '✅' : '⏸️'}
      leaderboardHref="/leaderboard"
      homeHref={hubUrl()}
    />;
  }

  return (
    <>
      <div style={{
        ...styles.pageWrapper,
        paddingBottom: gameStarted ? "max(300px, env(safe-area-inset-bottom) + 300px)" : "80px",
      }}>
        <div style={styles.cardContainer}>
          {/* Header */}
          <div style={{ ...styles.header, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ width: "32px", height: "32px", backgroundColor: "#2563eb", borderRadius: "6px", border: "2px solid rgba(0,0,0,0.12)", boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ color: "white", fontFamily: "'Inter', sans-serif", fontWeight: "900", fontSize: "0.95rem", letterSpacing: "-0.02em", lineHeight: 1 }}>C</span>
              </div>
              <div style={{ fontSize: "1.5rem", lineHeight: 1, letterSpacing: "-0.02em" }}>
                <span style={{ color: "#333", fontWeight: "500" }}>Connect</span><span style={{ color: "#2563eb", fontWeight: "900" }}>Pro</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <Link
                to="/how-to-play"
                style={styles.leaderboardLink}
                onMouseEnter={(e) => e.target.style.color = "#555"}
                onMouseLeave={(e) => e.target.style.color = "#888"}
              >
                ❓ Help
              </Link>
              <a
                href="/leaderboard"
                style={styles.leaderboardLink}
                onMouseEnter={(e) => e.target.style.color = "#555"}
                onMouseLeave={(e) => e.target.style.color = "#888"}
              >
                🏆 Leaderboard
              </a>
              <a
                href={buildProLink}
                style={{ ...styles.leaderboardLink, color: "#0f766e", fontWeight: 700 }}
                onMouseEnter={(e) => e.target.style.color = "#115e59"}
                onMouseLeave={(e) => e.target.style.color = "#0f766e"}
              >
                BuildPro
              </a>
            </div>
          </div>

          {/* Meta info */}
          <div style={styles.metaRow}>
            <span>{puzzleDate}</span>
            <span>{playerName}</span>
          </div>

          {/* Stats bar */}
          <div style={styles.statBar}>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Time</div>
              <div style={styles.statValue}>{formatTime(elapsedTime)}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Guesses</div>
              <div style={styles.statValue}>{guessCount}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Hints</div>
              <div style={styles.statValue}>{hintsUsed}</div>
            </div>
          </div>

          {/* Start word */}
          <div style={styles.wordBadge}>
            {gameStarted ? startWord : "?"}
          </div>

          {/* Word inputs */}
          <style>{`
            @keyframes caretBlink {
              0%, 49% { opacity: 1; }
              50%, 100% { opacity: 0; }
            }
            @keyframes rowPulse {
              0% { box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.12), inset 0 0 0 1px rgba(25, 118, 210, 0.08); }
              50% { box-shadow: 0 0 0 6px rgba(25, 118, 210, 0.2), inset 0 0 0 1px rgba(25, 118, 210, 0.12); }
              100% { box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.12), inset 0 0 0 1px rgba(25, 118, 210, 0.08); }
            }
          `}</style>
          <div style={styles.wordInputsContainer}>
            {guesses.map((guess, idx) => {
              const isActive = activeRow === idx && gameStarted;
              const isCorrect = validationResults[idx] === true;
              const isWrong = submitted && validationResults[idx] === false;
              const isLocked = isCorrect; // Correct rows are locked

              return (
                <div key={idx} style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "4px" }}>
                  <div
                    onClick={() => gameStarted && !isLocked && setActiveRow(idx)}
                    style={{
                      ...styles.wordInput,
                      flex: 1,
                      margin: "0",
                      borderColor: isCorrect
                        ? "#16a34a"
                        : isWrong
                        ? "#dc2626"
                        : isActive
                        ? "#2563eb"
                        : "#e5e7eb",
                      borderWidth: "2px",
                      backgroundColor: isCorrect
                        ? "#f0fdf4"
                        : isWrong
                        ? "#fef2f2"
                        : isActive
                        ? "#eff6ff"
                        : "#ffffff",
                      opacity: isLocked ? 0.75 : gameStarted ? 1 : 0.6,
                      cursor: isLocked ? "not-allowed" : gameStarted ? "pointer" : "default",
                      boxShadow: isActive && !isLocked
                        ? "0 0 0 4px rgba(37, 99, 235, 0.12), inset 0 0 0 1px rgba(37, 99, 235, 0.08)"
                        : isCorrect
                        ? "0 1px 3px rgba(22, 163, 74, 0.1)"
                        : "0 1px 2px rgba(0, 0, 0, 0.04)",
                      transform: isActive ? "scale(1.01)" : "scale(1)",
                      animation: isActive && !isLocked ? "rowPulse 0.6s ease-out" : "none",
                    }}
                  >
                    <span style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                      {guess}
                      {isActive && gameStarted && !isLocked && (
                        <span
                          style={{
                            display: "inline-block",
                            width: "2px",
                            height: "28px",
                            backgroundColor: "#2563eb",
                            marginLeft: "4px",
                            borderRadius: "1px",
                            animation: "caretBlink 1s infinite",
                          }}
                        />
                      )}
                    </span>
                  </div>

                  {/* Per-row hint button */}
                  <button
                    onClick={() => handleRowHint(idx)}
                    disabled={isLocked || !gameStarted}
                    style={{
                      padding: "6px 8px",
                      fontSize: "16px",
                      border: "1.5px solid #e5e7eb",
                      borderRadius: "8px",
                      backgroundColor: isLocked ? "#f3f4f6" : "#ffffff",
                      cursor: isLocked || !gameStarted ? "not-allowed" : "pointer",
                      color: isLocked ? "#ccc" : "#333",
                      fontWeight: "600",
                      transition: "all 0.2s ease",
                      minHeight: "50px",
                      minWidth: "50px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isLocked && gameStarted) {
                        e.target.style.backgroundColor = "#f0f4ff";
                        e.target.style.borderColor = "#dbeafe";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    💡
                  </button>
                </div>
              );
            })}
          </div>

          {/* End word */}
          <div style={styles.wordBadge}>
            {gameStarted ? endWord : "?"}
          </div>

          {/* Buttons */}
          {!gameStarted ? (
            <button
              onClick={handleStartGame}
              type="button"
              style={{
                ...styles.primaryButton,
                ...styles.primaryButtonStart,
                width: "100%",
                marginBottom: "2px",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#1565C0"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#1976D2"}
            >
              Start Game
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              type="button"
              style={{
                ...styles.primaryButton,
                ...styles.primaryButtonSubmit,
                width: "100%",
                marginBottom: "2px",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#45a049"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#4CAF50"}
            >
              Submit
            </button>
          )}
          {duplicateMessage && (
            <div style={{ color: "#e65100", fontSize: "0.8rem", textAlign: "center", marginTop: "6px" }}>
              {duplicateMessage}
            </div>
          )}
        </div>
      </div>

      {/* Fixed keyboard at bottom */}
      <div style={styles.keyboardWrapper}>
        <Keyboard
          onLetterClick={handleKeyboardLetter}
          onBackspaceClick={handleKeyboardBackspace}
          onClearClick={handleKeyboardClear}
          onEnterClick={handleKeyboardEnter}
          disabled={!gameStarted}
        />
      </div>

      {/* Completion Modal */}
      {showCompletionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from { transform: translateY(20px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px 24px",
              maxWidth: "420px",
              width: "100%",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
              animation: "slideUp 0.4s ease-out",
            }}
          >
            {/* Title */}
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "800",
                margin: "0 0 8px 0",
                color: "#16a34a",
                textAlign: "center",
              }}
            >
              🎉 Puzzle Complete!
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#999",
                textAlign: "center",
                margin: "0 0 24px 0",
              }}
            >
              Great job solving the puzzle!
            </p>

            {/* Chain Display */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "24px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#555",
                letterSpacing: "0.5px",
                wordBreak: "break-word",
              }}
            >
              <span style={{ color: "#0a0a0a", fontWeight: "800" }}>{startWord.toUpperCase()}</span>
              {guesses.map((guess, idx) => (
                <span key={idx}>
                  {" → "}
                  <span style={{ color: "#0a0a0a", fontWeight: "800" }}>{guess}</span>
                </span>
              ))}
              {" → "}
              <span style={{ color: "#0a0a0a", fontWeight: "800" }}>{endWord.toUpperCase()}</span>
            </div>

            {/* Stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    marginBottom: "6px",
                  }}
                >
                  Hints
                </div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: "#0a0a0a" }}>
                  {hintsUsed}
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    marginBottom: "6px",
                  }}
                >
                  Time
                </div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: "#0a0a0a" }}>
                  {formatTime(elapsedTime)}
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    marginBottom: "6px",
                  }}
                >
                  Guesses
                </div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: "#0a0a0a" }}>
                  {guessCount}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => (window.location.href = "/leaderboard")}
                style={{
                  flex: 1,
                  padding: "14px 16px",
                  fontSize: "15px",
                  fontWeight: "800",
                  border: "none",
                  borderRadius: "10px",
                  backgroundColor: "#2563eb",
                  color: "white",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#2563eb")}
              >
                View Leaderboard
              </button>
              <button
                onClick={() => {
                  const text = `I solved ConnectPro in ${formatTime(elapsedTime)} with ${guessCount} guesses and ${hintsUsed} hints!`;
                  if (navigator.share) {
                    navigator.share({
                      title: "ConnectPro",
                      text: text,
                    });
                  } else {
                    navigator.clipboard.writeText(text);
                    alert("Result copied to clipboard!");
                  }
                }}
                style={{
                  flex: 1,
                  padding: "14px 16px",
                  fontSize: "15px",
                  fontWeight: "800",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "10px",
                  backgroundColor: "white",
                  color: "#333",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f8f9fa";
                  e.target.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                  e.target.style.borderColor = "#e5e7eb";
                }}
              >
                Share
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ConnectPro Login Screen
const ConnectProLoginScreen = ({ onLogin }) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const verifyOrReserve = async (trimmedName, pwd) => {
    try {
      console.log("🔐 [LOGIN] Starting verify for:", trimmedName);
      const verifyResponse = await fetch(`${process.env.REACT_APP_API_URL}/verify-name/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: trimmedName, password: pwd }),
      });
      console.log("🔐 [LOGIN] Verify response status:", verifyResponse.status);

      if (verifyResponse.status === 404) {
        console.log("🔐 [LOGIN] Name not found (404), attempting registration...");
        const reserveResponse = await fetch(`${process.env.REACT_APP_API_URL}/reserve-name/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ player_name: trimmedName, password: pwd }),
        });
        console.log("🔐 [LOGIN] Reserve response status:", reserveResponse.status);
        const reserveData = await reserveResponse.json();
        console.log("🔐 [LOGIN] Reserve response data:", reserveData);
        return reserveResponse.ok;
      } else {
        const verifyData = await verifyResponse.json();
        console.log("🔐 [LOGIN] Verify response data:", verifyData);
        const success = verifyResponse.ok && verifyData.success === true;
        console.log("🔐 [LOGIN] Verify success:", success);
        return success;
      }
    } catch (err) {
      console.error("❌ [LOGIN] Exception in verifyOrReserve:", err);
      setPasswordError("Network error: " + err.message);
      return false;
    }
  };

  const handleStart = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { alert("Please enter your name."); return; }
    if (trimmedName.length > 20) { alert("Name must be 20 characters or fewer."); return; }
    if (!password.trim()) { setPasswordError("Please enter your password."); return; }

    console.log("🔐 [LOGIN] Button clicked, starting flow...");
    setIsLoading(true);
    setPasswordError("");

    const success = await verifyOrReserve(trimmedName, password);
    console.log("🔐 [LOGIN] Final success result:", success);
    setIsLoading(false);

    if (!success) {
      console.log("🔐 [LOGIN] Login failed");
      setPasswordError("Incorrect password or name is taken.");
      return;
    }

    console.log("🔐 [LOGIN] Setting localStorage and calling onLogin...");
    localStorage.setItem("playerName", trimmedName);
    onLogin(trimmedName);
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", fontSize: "16px",
    borderRadius: "8px", border: "1px solid #d3d6da",
    boxSizing: "border-box", marginBottom: "12px",
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backgroundColor: "#f4f4f4" }}>
      <div style={{ backgroundColor: "white", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.08)", padding: "28px 20px", width: "100%", maxWidth: "440px", boxSizing: "border-box" }}>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <div style={{ width: "36px", height: "36px", backgroundColor: "#2563eb", borderRadius: "6px", border: "2px solid rgba(0,0,0,0.12)", boxShadow: "inset 0 -3px 0 rgba(0,0,0,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: "white", fontFamily: "'Inter', sans-serif", fontWeight: "900", fontSize: "1.1rem", letterSpacing: "-0.02em", lineHeight: 1 }}>C</span>
          </div>
          <h1 style={{ fontSize: "2.2rem", margin: 0, fontFamily: "'Inter', sans-serif", lineHeight: 1, letterSpacing: "-0.03em" }}>
            <span style={{ color: "#333", fontWeight: "500" }}>Connect</span><span style={{ color: "#2563eb", fontWeight: "900" }}>Pro</span>
          </h1>
        </div>

        <p style={{ fontSize: "0.85rem", color: "#888", marginBottom: "20px", lineHeight: "1.5", marginTop: "4px" }}>
          <strong style={{ color: "#333" }}>A daily word-chain puzzle game.</strong><br />
          Find the words that connect. Enter your name to play.
        </p>

        <input
          type="text"
          placeholder="Enter your name"
          value={name}
          maxLength={20}
          onChange={(e) => { setName(e.target.value); }}
          style={inputStyle}
          onKeyDown={(e) => { if (e.key === "Enter") handleStart(); }}
        />
        <input
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") handleStart(); }}
          style={inputStyle}
        />
        {passwordError && (
          <p style={{ color: "red", marginTop: "-8px", marginBottom: "12px", fontSize: "0.85rem" }}>{passwordError}</p>
        )}

        <button
          onClick={handleStart}
          disabled={isLoading}
          style={{ width: "100%", padding: "14px", fontSize: "1.1rem", fontWeight: "bold", backgroundColor: isLoading ? "#9ca3af" : "#2563eb", color: "white", border: "none", borderRadius: "8px", cursor: isLoading ? "wait" : "pointer", marginTop: "4px", boxShadow: isLoading ? "none" : "0 2px 0 #1d4ed8", transition: "filter 0.15s ease", opacity: isLoading ? 0.8 : 1 }}
          onMouseOver={(e) => !isLoading && (e.currentTarget.style.filter = "brightness(1.05)")}
          onMouseOut={(e) => !isLoading && (e.currentTarget.style.filter = "none")}
        >
          {isLoading ? "Logging in..." : "Play"}
        </button>
      </div>
    </div>
  );
};

export default ConnectProGame;
